/**
 * Partner access card — buyer side (Supplier profile page).
 *
 * Drives the invite-code lifecycle for connecting a supplier record to that
 * supplier's own organisation in the IMS:
 *  - no junction / revoked → "Invite partner organisation" → one-time code
 *    dialog (code is shown ONCE — only the scrypt hash is stored),
 *  - pending → expiry note + "Rotate code" (re-invite resets the hash),
 *  - active → partner org name + connected-since + destructive "Revoke access".
 *
 * Admin-only (the backend procedures are adminProcedure); the parent gates
 * rendering on isAdmin. Hides itself when the SUPPLIER_PORTAL_ENABLED flag is
 * off (the status query reports enabled:false).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy, Handshake, Loader2, RefreshCw, ShieldOff } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : '-';

export function PartnerAccessCard({ supplierId }: { supplierId: string }) {
  const utils = trpc.useUtils();

  const statusQuery = trpc.supplierPortal.getSupplierPartnerStatus.useQuery(
    { supplierId },
    { staleTime: 30_000 },
  );

  const [inviteResult, setInviteResult] = useState<{ code: string; expiresAt: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);

  const invalidate = () =>
    void utils.supplierPortal.getSupplierPartnerStatus.invalidate({ supplierId });

  const generateInvite = trpc.supplierPortal.generateSupplierInvite.useMutation({
    onSuccess: (result) => {
      setCopied(false);
      setInviteResult({ code: result.inviteCode, expiresAt: result.expiresAt });
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const revokeAccess = trpc.supplierPortal.revokeSupplierAccess.useMutation({
    onSuccess: () => {
      toast.success('Partner access revoked');
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const status = statusQuery.data;
  // Flag off (enabled:false) or not loaded yet → render nothing; the card is
  // an optional enhancement, not core supplier data.
  if (!status?.enabled) return null;

  const handleCopy = async () => {
    if (!inviteResult) return;
    try {
      await navigator.clipboard.writeText(inviteResult.code);
      setCopied(true);
      toast.success('Invite code copied');
    } catch {
      toast.error('Copy failed. Select the code and copy it manually');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Handshake className="h-4 w-4" />
            Partner access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {status.status === 'active' ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {status.partnerOrganizationName ?? 'Partner organisation'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Connected since {formatDate(status.redeemedAt)}
                  </p>
                </div>
                <Badge variant="success">Connected</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                This organisation can see the purchase orders and product-dev records where
                this supplier is assigned, never costs or pricing.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setRevokeOpen(true)}
                disabled={revokeAccess.isPending}
              >
                {revokeAccess.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <ShieldOff className="h-3.5 w-3.5 mr-1.5" />
                )}
                Revoke access
              </Button>
            </>
          ) : status.status === 'pending' ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Invite pending</p>
                  <p className="text-xs text-muted-foreground">
                    Expires {formatDate(status.inviteExpiresAt)}
                  </p>
                </div>
                <Badge variant="secondary">Pending</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Waiting for the partner to redeem the invite code in their Settings →
                Organization page. Rotating generates a fresh code and voids the old one.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => generateInvite.mutate({ supplierId })}
                disabled={generateInvite.isPending}
              >
                {generateInvite.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Rotate code
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {status.status === 'revoked'
                  ? `Access was revoked on ${formatDate(status.revokedAt)}. Invite again to reconnect.`
                  : 'Give this supplier a scoped view of the orders you place with them: they can acknowledge POs, update production status, set ETAs, and collaborate on product dev. Costs and pricing are never shared.'}
              </p>
              <Button
                type="button"
                size="sm"
                onClick={() => generateInvite.mutate({ supplierId })}
                disabled={generateInvite.isPending}
              >
                {generateInvite.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Handshake className="h-3.5 w-3.5 mr-1.5" />
                )}
                Invite partner organisation
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* One-time code dialog */}
      <Dialog
        open={inviteResult !== null}
        onOpenChange={(open) => {
          if (!open) setInviteResult(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Partner invite code</DialogTitle>
            <DialogDescription>
              This code is shown once. Share it with your partner now. They redeem it in
              Settings → Organization → Partnerships. Expires{' '}
              {inviteResult ? formatDate(inviteResult.expiresAt) : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md border bg-muted px-3 py-2 font-mono text-sm tracking-wider break-all">
              {inviteResult?.code}
            </code>
            <Button type="button" variant="outline" size="icon" onClick={() => void handleCopy()}>
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setInviteResult(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke partner access?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{status.partnerOrganizationName ?? 'The partner organisation'}</strong>{' '}
              immediately loses access to every shared order and product-dev record. You can
              invite them again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => revokeAccess.mutate({ supplierId, reason: null })}
            >
              Revoke access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
